/**
 * MarketFitSettingsDrawer
 * 
 * A drawer panel for configuring deal settings including:
 * - Edit deal name
 * - Change solution
 * - Change situation
 * - Change actor
 * - Copy deal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Form, Input, Select, Button, Divider, message, Spin, Popconfirm } from 'antd';
import {
  SettingOutlined,
  EditOutlined,
  SolutionOutlined,
  BulbOutlined,
  UserOutlined,
  CopyOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { useMarketFit } from '../../contexts/MarketFitContext';
import styles from './MarketFitSettingsDrawer.module.css';

interface MarketFitSettingsDrawerProps {
  visible: boolean;
  onClose: () => void;
  dealId: string;
  productId: string;
  onDealCopied?: (newDealId: string, newDealName: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const SOLUTION_ARTIFACT_TYPE = 'ProductSolution';
const SITUATION_ARTIFACT_TYPE = 'CustomerSituation';
const ACTOR_ARTIFACT_TYPE = 'CustomerActor';
const DEAL_ARTIFACT_TYPE = 'Deal';

export const MarketFitSettingsDrawer: React.FC<MarketFitSettingsDrawerProps> = ({
  visible,
  onClose,
  dealId,
  productId,
  onDealCopied,
}) => {
  const [form] = Form.useForm();
  const { deal, solution, situation, actor } = useMarketFit();
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [copying, setCopying] = useState(false);
  const [solutions, setSolutions] = useState<SelectOption[]>([]);
  const [situations, setSituations] = useState<SelectOption[]>([]);
  const [actors, setActors] = useState<SelectOption[]>([]);

  // Fetch options when drawer opens
  const fetchOptions = useCallback(async () => {
    if (!visible) return;
    
    setLoadingOptions(true);
    try {
      // Fetch all three artifact types in parallel
      const [solutionsRes, situationsRes, actorsRes] = await Promise.all([
        apiClient.get(`/artifacts`, { params: { artifact_type: SOLUTION_ARTIFACT_TYPE, company_id: productId } }),
        apiClient.get(`/artifacts`, { params: { artifact_type: SITUATION_ARTIFACT_TYPE, company_id: productId } }),
        apiClient.get(`/artifacts`, { params: { artifact_type: ACTOR_ARTIFACT_TYPE, company_id: productId } }),
      ]);

      const toOptions = (res: any): SelectOption[] => {
        const list = Array.isArray(res.data) ? res.data : res.data?.artifacts || [];
        return list.map((item: any) => ({
          value: item.id,
          label: item.name || 'Unnamed',
        }));
      };

      setSolutions(toOptions(solutionsRes));
      setSituations(toOptions(situationsRes));
      setActors(toOptions(actorsRes));
    } catch (error) {
      console.error('Failed to fetch options:', error);
    } finally {
      setLoadingOptions(false);
    }
  }, [visible, productId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // Set initial form values when deal data is available
  useEffect(() => {
    if (visible && deal) {
      form.setFieldsValue({
        name: deal.name,
        solutionId: deal.productSolutionId,
        situationId: deal.customerSituationId,
        actorId: deal.customerActorId,
      });
    }
  }, [visible, deal, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Update the deal artifact
      await apiClient.patch(`/artifacts/${dealId}`, {
        name: values.name,
        artifact_metadata: {
          product_solution_id: values.solutionId || null,
          customer_situation_id: values.situationId || null,
          customer_actor_id: values.actorId || null,
        },
      });

      void message.success('Deal settings saved');
      onClose();
    } catch (error: any) {
      console.error('Failed to save deal settings:', error);
      void message.error(error.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyDeal = async () => {
    try {
      setCopying(true);
      const values = form.getFieldsValue();
      const copyName = `${values.name} (Copy)`;

      // Create a copy of the deal
      const res = await apiClient.post('/artifacts', {
        name: copyName,
        artifact_type: DEAL_ARTIFACT_TYPE,
        company_id: productId,
        artifact_metadata: {
          product_solution_id: values.solutionId || deal?.productSolutionId || null,
          customer_situation_id: values.situationId || deal?.customerSituationId || null,
          customer_actor_id: values.actorId || deal?.customerActorId || null,
          connection_links: deal?.connectionLinks || {
            solutionInputToSituationResourceLinks: [],
            solutionBenefitToSituationNeedLinks: [],
          },
          deal_score: 0,
          product_situation_alignment: 0,
          product_situation_value: 0,
          actor_situation_alignment: deal?.scores?.actorSituationAlignment || 0,
        },
      });

      if (res.data && res.data.id) {
        void message.success(`Deal copied as "${copyName}"`);
        onDealCopied?.(res.data.id, copyName);
      }
    } catch (error: any) {
      console.error('Failed to copy deal:', error);
      void message.error(error.message || 'Failed to copy deal');
    } finally {
      setCopying(false);
    }
  };

  return (
    <Drawer
      title={
        <div className={styles.drawerTitle}>
          <SettingOutlined className={styles.titleIcon} />
          <span>Deal Settings</span>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={400}
      className={styles.drawer}
      footer={
        <div className={styles.footer}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <Spin spinning={loadingOptions}>
        <Form
          form={form}
          layout="vertical"
          className={styles.form}
        >
          {/* Deal Name */}
          <Form.Item
            name="name"
            label={
              <span className={styles.label}>
                <EditOutlined /> Deal Name
              </span>
            }
            rules={[{ required: true, message: 'Please enter a deal name' }]}
          >
            <Input placeholder="Enter deal name" />
          </Form.Item>

          <Divider className={styles.divider} />

          {/* Solution Selection */}
          <Form.Item
            name="solutionId"
            label={
              <span className={styles.label}>
                <SolutionOutlined /> Product Solution
              </span>
            }
          >
            <Select
              placeholder="Select a solution"
              options={solutions}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          {/* Current Solution Info */}
          {solution && (
            <div className={styles.currentInfo}>
              Current: <strong>{solution.name}</strong>
            </div>
          )}

          <Divider className={styles.divider} />

          {/* Situation Selection */}
          <Form.Item
            name="situationId"
            label={
              <span className={styles.label}>
                <BulbOutlined /> Customer Situation
              </span>
            }
          >
            <Select
              placeholder="Select a situation"
              options={situations}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          {/* Current Situation Info */}
          {situation && (
            <div className={styles.currentInfo}>
              Current: <strong>{situation.name}</strong>
            </div>
          )}

          <Divider className={styles.divider} />

          {/* Actor Selection */}
          <Form.Item
            name="actorId"
            label={
              <span className={styles.label}>
                <UserOutlined /> Customer Actor
              </span>
            }
          >
            <Select
              placeholder="Select an actor"
              options={actors}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          {/* Current Actor Info */}
          {actor && (
            <div className={styles.currentInfo}>
              Current: <strong>{actor.actorName}</strong> ({actor.actorRole})
            </div>
          )}

          <Divider className={styles.divider} />

          {/* Copy Deal */}
          <div className={styles.copySection}>
            <Popconfirm
              title="Copy this deal?"
              description="This will create a new deal with the same settings."
              onConfirm={handleCopyDeal}
              okText="Copy"
              cancelText="Cancel"
            >
              <Button
                icon={<CopyOutlined />}
                loading={copying}
                className={styles.copyButton}
              >
                Make a Copy of this Deal
              </Button>
            </Popconfirm>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default MarketFitSettingsDrawer;
